import { Search, X } from "lucide-react";
import { AdminToolbar, FilterPills, type FilterPill } from "@/components/admin";
import { Input } from "@/components/ui/input";
import { OrganizationCombobox } from "@/components/shared/OrganizationCombobox";
import { useMemo } from "react";

export interface DocumentsToolbarProps {
    search: string;
    onSearchChange: (value: string) => void;
    organizationId: number | null;
    organizationName: string | null;
    onOrgIdChange: (orgId: number | null) => void;
    onOrgNameChange: (name: string | null) => void;
}

export function DocumentsToolbar({
    search,
    onSearchChange,
    organizationId,
    organizationName,
    onOrgIdChange,
    onOrgNameChange,
}: DocumentsToolbarProps) {
    const filters = useMemo(() => {
        const pills: FilterPill[] = [];
        if (search) {
            pills.push({ id: "search", label: `Search: ${search}` });
        }
        if (organizationId !== null && organizationName) {
            pills.push({ id: "org", label: organizationName });
        }
        return pills;
    }, [search, organizationId, organizationName]);

    const handleRemoveFilter = (id: string) => {
        if (id === "search") onSearchChange("");
        if (id === "org") {
            onOrgIdChange(null);
            onOrgNameChange(null);
        }
    };

    const handleClearAll = () => {
        onSearchChange("");
        onOrgIdChange(null);
        onOrgNameChange(null);
    };

    return (
        <div className="mb-4 space-y-3">
            <AdminToolbar>
                <div className="relative w-64">
                    <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
                    <Input
                        placeholder="Search documents..."
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        className="h-8 pr-8 pl-8 text-sm"
                    />
                    {search && (
                        <button
                            type="button"
                            onClick={() => onSearchChange("")}
                            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2 transition-colors"
                        >
                            <X className="size-3.5" />
                        </button>
                    )}
                </div>
                <OrganizationCombobox
                    value={organizationId}
                    onChange={onOrgIdChange}
                    onChangeName={onOrgNameChange}
                />
            </AdminToolbar>
            <FilterPills
                filters={filters}
                onRemove={handleRemoveFilter}
                onClearAll={handleClearAll}
            />
        </div>
    );
}
